<script setup>
import { storeToRefs } from "pinia";
import { ErrorMessage, Field, Form } from "vee-validate";
import { computed } from "vue";
import { useI18n } from "vue-i18n";

import TgButton from "@/abc-dsk/components/Button/TgButton.vue";
import TgModal from "@/abc-dsk/components/Modal/TgModal.vue";
import { Size } from "@/abc-dsk/options";
import { ModalType } from "@/common/vocabulary-trainer-helpers";
import { useAuthStore } from "@/stores";

const props = defineProps({
  modelValue: {
    type: String,
    default: ""
  },
  title: {
    type: String,
    required: true
  },
  bodyText: {
    type: String,
    required: true
  },
  inputPlaceholder: {
    type: String,
    required: true
  },
  listName: {
    type: String,
    default: ""
  },
  showInput: {
    type: Boolean,
    // eslint-disable-next-line vue/no-boolean-default
    default: true
  },
  showName: {
    type: Boolean,
    default: false
  }
});

const emit = defineEmits(["close", "submit", "update:modelValue"]);

const { t } = useI18n();

const authStore = useAuthStore();
const { userIsTeacher } = storeToRefs(authStore);

const modalType = computed(() => {
  if (!userIsTeacher.value) {
    return ModalType.AddParticipant;
  }

  // Slightly janky solution to figuring out the type, but it's the best that we have without involving the router.
  return props.showInput ? ModalType.Create : ModalType.AddParticipant;
});

const inputValue = computed({
  get() {
    return props.modelValue;
  },
  set(value) {
    return emit("update:modelValue", value);
  }
});

const onSubmit = () => emit("submit", { input: inputValue.value, type: modalType.value });
</script>

<template>
  <TgModal :size="Size.Sm" :lazy="false" @close="emit('close')">
    <template #modal-header>
      <span>{{ props.title }}</span>
    </template>
    <div class="create-or-add-list-modal">
      <div v-if="modalType === ModalType.AddParticipant" class="create-or-add-list-modal__body">
        <div v-if="props.bodyText" class="create-or-add-list-modal__body-text">{{ bodyText }}</div>
        <div v-if="props.showName" class="create-or-add-list-modal__body-list-name">
          {{ props.listName }}
        </div>
      </div>
      <Form class="create-or-add-list-modal__form" @submit="onSubmit()">
        <div class="d-flex flex-column">
          <Field
            v-show="showInput"
            v-model="inputValue"
            name="input-field"
            class="create-or-add-list-modal__input"
            type="text"
            :aria-label="props.inputPlaceholder"
            :placeholder="props.inputPlaceholder"
            rules="required"
          />
          <ErrorMessage name="input-field" class="create-or-add-list-modal__input-error" />
        </div>
        <TgButton type="submit">{{ t("vocab.home.modal.common.add") }}</TgButton>
      </Form>
      <TgButton
        class="create-or-add-list-modal__cancel"
        variant="text"
        color="grey"
        @click="emit('close')"
        >{{ t("vocab.home.modal.common.cancel") }}</TgButton
      >
    </div>
  </TgModal>
</template>

<style lang="less" scoped>
@import "@/abc-dsk/theming/index.less";

.create-or-add-list-modal {
  text-align: center;

  &__body {
    &-text {
      margin-bottom: 5px;
      color: rgb(@theme-colors[@grey][base]);
    }

    &-list-name {
      font-weight: bold;
    }
  }

  &__form {
    display: flex;
    flex-direction: column;
    gap: 20px;
  }

  &__input {
    padding: 10px 12px;
    border: 1px solid rgb(@theme-colors[@grey][50]);
    border-radius: 4px;
    text-align: center;

    &::placeholder {
      color: rgb(@theme-colors[@grey][50]);
    }
  }

  &__input-error {
    margin-top: 5px;
    color: rgb(@theme-colors[@red][base]);
  }

  &__cancel {
    margin-top: 20px;
  }
}
</style>
